/**
 * OEM Database Service
 * Handles offline-first storage for OEM branding configurations
 */
import IndexedDBService from './indexedDBService';

interface OEMConfig {
  oemId: string;
  brandName: string;
  customDomain: string;
  contactInfo: {
    address: string;
    phone: string;
    email: string;
  };
  logo?: {
    url: string;
    localPath: string;
    uploadedAt: number;
  };
  theme?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  translations?: {
    en?: {
      brandName?: string;
      welcomeMessage?: string;
    };
    fr?: {
      brandName?: string;
      welcomeMessage?: string;
    };
  };
  createdAt: number;
  updatedAt: number;
  active: boolean;
}

interface OEMLogo {
  oemId: string;
  logoBlob: Blob;
  mimeType: string;
  uploadedAt: number;
}

class OEMDatabase extends IndexedDBService {
  constructor() {
    super('OEMDB', 1);
  }

  protected setupObjectStores(db: IDBDatabase): void {
    // OEM configurations store
    if (!db.objectStoreNames.contains('oem_configs')) {
      const oemConfigStore = db.createObjectStore('oem_configs', { keyPath: 'oemId' });
      oemConfigStore.createIndex('customDomain', 'customDomain', { unique: true });
      oemConfigStore.createIndex('active', 'active', { unique: false });
    }

    // OEM logos store (binary data)
    if (!db.objectStoreNames.contains('oem_logos')) {
      db.createObjectStore('oem_logos', { keyPath: 'oemId' });
    }
  }

  /**
   * Save OEM configuration
   */
  async saveOEMConfig(config: OEMConfig): Promise<void> {
    await this.put('oem_configs', config);
  }

  /**
   * Get OEM configuration by ID
   */
  async getOEMConfig(oemId: string): Promise<OEMConfig | null> {
    const config = await this.get('oem_configs', oemId);
    return config || null;
  }

  /**
   * Get OEM configuration by custom domain
   */
  async getOEMByDomain(domain: string): Promise<OEMConfig | null> {
    const configs = await this.queryByIndex('oem_configs', 'customDomain', domain);
    return configs.length > 0 ? configs[0] : null;
  }

  /**
   * Get all OEM configurations
   */
  async getAllOEMConfigs(): Promise<OEMConfig[]> {
    return await this.getAll('oem_configs');
  }

  /**
   * Get all active OEM configurations
   */
  async getActiveOEMConfigs(): Promise<OEMConfig[]> {
    return await this.queryByIndex('oem_configs', 'active', true);
  }

  /**
   * Delete OEM configuration
   */
  async deleteOEMConfig(oemId: string): Promise<void> {
    await this.delete('oem_configs', oemId);
    // Also delete associated logo
    await this.deleteOEMLogo(oemId);
  }

  /**
   * Save OEM logo (binary data)
   */
  async saveOEMLogo(oemId: string, logoBlob: Blob, mimeType: string): Promise<void> {
    const logoData: OEMLogo = {
      oemId,
      logoBlob,
      mimeType,
      uploadedAt: Date.now(),
    };
    await this.put('oem_logos', logoData);
  }

  /**
   * Get OEM logo
   */
  async getOEMLogo(oemId: string): Promise<OEMLogo | null> {
    const logo = await this.get('oem_logos', oemId);
    return logo || null;
  }

  /**
   * Delete OEM logo
   */
  async deleteOEMLogo(oemId: string): Promise<void> {
    try {
      await this.delete('oem_logos', oemId);
    } catch (error) {
      console.warn(`Failed to delete logo for OEM ${oemId}:`, error);
    }
  }

  /**
   * Clear all OEM data (use with caution)
   */
  async clearAllOEMData(): Promise<void> {
    await this.clear('oem_configs');
    await this.clear('oem_logos');
  }
}

// Export singleton instance
const oemDB = new OEMDatabase();
export default oemDB;
export type { OEMConfig, OEMLogo };
